import { ThemeProvider } from '@ori-ui/mui';
import { doDevelopmentLogin, NOT_LOGGED_IN_USER_ID, UniversalTokenProvider } from '@ori/auth';
import type { PickClientAppDataConfigProps } from '@ori/client-app-config';
import { parseClientAppConfig } from '@ori/client-app-config';
import { ServiceUrlsProvider } from '@ori/configuration-manager';
import { ClientCultureContextProvider } from '@ori/culture-provider';
import { EnvironmentProvider } from '@ori/environment';
import type { FooterAppProps, AppConfig } from '@ori/footer';
import {
  defaultTranslationsWithNamespaces,
  EMOTION_CACHE_KEY,
  FooterApp,
  FooterAppSkeleton,
  FooterErrorBoundary,
  languageSwitcherTranslationsNamespace,
  useLanguageSwitcherTranslations,
} from '@ori/footer';
import { TranslationsNamespaceProvider, TranslationsProvider } from '@ori/i18n';
import type { LanguageSwitcherPopupProps } from '@ori/language-switcher';
import { LanguageSwitcherPopup } from '@ori/language-switcher';
import type { FC } from 'react';
import { Suspense, useMemo } from 'react';
import { createRoot } from 'react-dom/client';

const LocalizedLanguageSwitcher: FC<Omit<LanguageSwitcherPopupProps, 'translations'>> = (props) => {
  const { translations } = useLanguageSwitcherTranslations();

  return (
    <LanguageSwitcherPopup
      {...props}
      translations={useMemo(
        () => ({
          SelectLanguage: translations.selectLanguage,
        }),
        [translations.selectLanguage],
      )}
    />
  );
};

/**
 * React app renderer that is used when app is used as a standalone app
 */
export async function render(rootElement: HTMLElement) {
  const { config = '' } = rootElement.dataset;
  const clientAppConfig = parseClientAppConfig<
    AppConfig &
      PickClientAppDataConfigProps<
        'currentLanguage' | 'customerId' | 'globalApiUrl' | 'graphqlUrl' | 'identityUrl' | 'staticApiUrl' | 'tenant'
      > & {
        disableLanguageSwitcherPopUp?: boolean;
      }
  >(config, ['currentLanguage', 'globalApiUrl', 'staticApiUrl', 'tenant']);

  const {
    currentLanguage,
    customerId = NOT_LOGGED_IN_USER_ID,
    disableLanguageSwitcherPopUp,
    globalApiUrl,
    graphqlUrl,
    identityUrl,
    isFrontPage,
    isStandalone = false,
    showFooterOnDesktop = true,
    staticApiUrl,
    tenant,
  } = clientAppConfig;

  const appProps: FooterAppProps = {
    configuration: {
      currentLanguage,
      isFrontPage,
      isStandalone,
      showFooterOnDesktop,
      tenant,
    },
  };

  const localizedLanguageSwitcherProps: Omit<LanguageSwitcherPopupProps, 'translations'> = {
    currentLanguage,
    staticApiUrl,
    tenant,
  };

  if (!showFooterOnDesktop) {
    return;
  }

  // Get token for local development
  if (process.env.NODE_ENV !== 'production') {
    // when using Requestly we do not want to load token
    if (process.env.REACT_APP_USE_REQUESTLY === '1') {
      // eslint-disable-next-line no-console -- wanted behavior
      console.log('USING REQUESTLY');
    } else {
      if (!identityUrl) {
        throw new Error('Missing Identity URL!');
      }

      await doDevelopmentLogin({ identityUrl, tenant });
    }
  }

  createRoot(rootElement).render(
    <FooterErrorBoundary>
      <ServiceUrlsProvider serviceUrls={{ graphQlUrl: graphqlUrl, globalApiUrl, staticApiUrl, identityUrl }}>
        <EnvironmentProvider tenant={tenant}>
          <UniversalTokenProvider
            customerId={customerId}
            initialToken=""
          >
            <ThemeProvider
              enableCacheProvider
              cacheKey={EMOTION_CACHE_KEY}
            >
              <ClientCultureContextProvider
                host={staticApiUrl}
                tenant={tenant}
              >
                <Suspense fallback={<FooterAppSkeleton />}>
                  <TranslationsProvider
                    defaultTranslationsWithNamespaces={defaultTranslationsWithNamespaces}
                    clientFetchConfiguration={{
                      baseUrl: staticApiUrl,
                      tenant,
                      // eslint-disable-next-line no-console -- we need to log errors
                      onError: console.error,
                    }}
                    language={currentLanguage}
                  >
                    {!disableLanguageSwitcherPopUp && (
                      <TranslationsNamespaceProvider namespace={languageSwitcherTranslationsNamespace}>
                        <LocalizedLanguageSwitcher {...localizedLanguageSwitcherProps} />
                      </TranslationsNamespaceProvider>
                    )}
                    <FooterApp {...appProps} />
                  </TranslationsProvider>
                </Suspense>
              </ClientCultureContextProvider>
            </ThemeProvider>
          </UniversalTokenProvider>
        </EnvironmentProvider>
      </ServiceUrlsProvider>
    </FooterErrorBoundary>,
  );
}
